import { useState, useEffect, useCallback } from "react";

const STORAGE_KEY = "tk_density";

export type Density = "comfortable" | "compact";

function getStoredDensity(): Density {
  if (typeof window === "undefined") return "comfortable";
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "compact" || stored === "comfortable") return stored;
  } catch {
    /* ignore */
  }
  return "comfortable";
}

function setStoredDensity(value: Density) {
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    /* ignore */
  }
}

/**
 * Hook for density (Comfortable vs Compact) with localStorage persistence.
 * Compact reduces row heights, paddings, gaps for more data on screen.
 * Only applies on md+ screens; mobile stays comfortable.
 */
export function useDensity() {
  const [density, setDensityState] = useState<Density>(getStoredDensity);

  const setDensity = useCallback((value: Density) => {
    setDensityState(value);
    setStoredDensity(value);
    // Update root class immediately (in case layout hasn't mounted yet)
    if (typeof document !== "undefined") {
      document.documentElement.classList.remove("density-compact", "density-comfortable");
      document.documentElement.classList.add(`density-${value}`);
    }
  }, []);

  useEffect(() => {
    document.documentElement.classList.remove("density-compact", "density-comfortable");
    document.documentElement.classList.add(`density-${density}`);
    return () => {
      document.documentElement.classList.remove("density-compact", "density-comfortable");
    };
  }, [density]);

  return {
    density,
    setDensity,
    isCompact: density === "compact",
  };
}
